import {
  Component,
  forwardRef,
  Input,
  OnChanges,
  SimpleChanges
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR
} from '@angular/forms';
import {
  AgpFormUtil,
  CvaFormGroupComponent,
  ResetOptions
} from '@liberty-financial/agp-shared';

import { ContactSummary } from '@features/contacts/models';
import { Relationship } from '@features/contacts/models/individual/relationship';
import { isStandardBaseDefinitionRequired } from '@shared/models/schema-property-definition';

interface RelationshipForm {
  contact: ContactSummary;
  new: boolean;
  deleted: boolean;
  type: string | null;
}

@Component({
  selector: 'spark-contacts-individual-relationships',
  templateUrl: './relationships.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => IndividualRelationshipsComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => IndividualRelationshipsComponent),
      multi: true
    }
  ]
})
export class IndividualRelationshipsComponent
  extends CvaFormGroupComponent<Relationship.Model[], RelationshipForm[]>
  implements OnChanges
{
  @Input() schema!: Relationship.SchemaPropertyDefinition;
  @Input() contacts: ContactSummary[] = [];
  @Input() isRequired = false;

  deleted: { [index: number]: boolean } = {};
  expandedIndex = -1;
  form = this.fb.array<
    FormGroup<{
      contact: FormControl<ContactSummary | null>;
      new: FormControl<boolean>;
      deleted: FormControl<boolean>;
      type: FormControl<string | null>;
    }>
  >([]);
  formGroup = this.fb.group({
    relationships: this.form
  });

  constructor(private readonly fb: FormBuilder) {
    super();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.isRequired) {
      AgpFormUtil.Validators.updateRequiredIfNecessary(this.form, changes.isRequired.currentValue);
    }
  }

  protected reset(opts?: ResetOptions | undefined): void {
    this.form?.reset(undefined, opts);
  }

  protected transformToModel = (
    value: RelationshipForm[]
  ): Relationship.Model[] => {
    return value.map((d) => ({
      ...d,
      created: d.new,
      id: d.contact?.id,
      value: d.type
    }));
  };

  protected transformToForm = (v: Relationship.Model[]): RelationshipForm[] => {
    const result: RelationshipForm[] = [];
    v.forEach((s) => {
      const contact = this.contacts.find((c) => c.id === s.id);
      if (contact) {
        this.add(false);
        result.push({ contact, new: s.created, deleted: s.deleted, type: s.value });
      }
    });
    return result;
  };

  add(newItem: boolean = true): void {
    const newItemForm = new FormGroup({
      contact: new FormControl<ContactSummary | null>(null),
      new: this.fb.nonNullable.control(newItem),
      deleted: this.fb.nonNullable.control<boolean>(false),
      type: new FormControl<string | null>(null)
    });
    AgpFormUtil.Validators.updateRequiredIfNecessary(
      newItemForm.controls.contact,
      isStandardBaseDefinitionRequired(this.schema)
    );
    AgpFormUtil.Validators.updateRequiredIfNecessary(
      newItemForm.controls.type,
      isStandardBaseDefinitionRequired(this.schema)
    );
    this.form.push(newItemForm);
    this.expandedIndex = this.form.length - 1;
  }

  remove(i: number): void {
    this.deleted[i] = true;
    this.form.at(i).controls.deleted.setValue(true);
  }
}
